import { useState, useEffect } from 'react';
import { useApi } from '../contexts/ApiContext';
import { useToast } from '../contexts/ToastContext';
import { usePermissions } from '../hooks/usePermissions';
import Modal from './Modal';
import { SkeletonTable } from './Skeleton';
import { EmptyStateNoData } from './EmptyState';
import { formatDate } from '../utils/dateFormatter';
import './BusinessRules.css';

export type RuleTriggerType = 
  | 'STOCK_BELOW_MIN' 
  | 'STOCK_ABOVE_MAX'
  | 'WORK_ORDER_COMPLETED'
  | 'PURCHASE_ORDER_RECEIVED'
  | 'COATING_ORDER_RECEIVED'
  | 'SALE_RECORDED'
  | 'DAILY_CHECK';

export type RuleActionType =
  | 'CREATE_NOTIFICATION'
  | 'CREATE_PURCHASE_ORDER'
  | 'UPDATE_PRIORITY'
  | 'SEND_EMAIL'
  | 'LOG_EVENT';

interface RuleCondition {
  field: string;
  operator: 'equals' | 'greater_than' | 'less_than' | 'contains' | 'in';
  value: any;
}

interface RuleAction {
  type: RuleActionType;
  params: Record<string, any>;
}

interface BusinessRule {
  id?: number;
  name: string;
  description?: string;
  trigger_type: RuleTriggerType;
  conditions?: RuleCondition[];
  actions: RuleAction[];
  is_active: boolean;
  priority: number;
  created_at?: string;
  updated_at?: string;
}

const TRIGGER_LABELS: Record<RuleTriggerType, string> = {
  STOCK_BELOW_MIN: 'Stock Bajo Mínimo',
  STOCK_ABOVE_MAX: 'Stock Sobre Máximo',
  WORK_ORDER_COMPLETED: 'Orden de Trabajo Completada',
  PURCHASE_ORDER_RECEIVED: 'Pedido Recibido',
  COATING_ORDER_RECEIVED: 'Pedido Revestimiento Recibido',
  SALE_RECORDED: 'Venta Registrada',
  DAILY_CHECK: 'Verificación Diaria',
};

const ACTION_LABELS: Record<RuleActionType, string> = {
  CREATE_NOTIFICATION: 'Crear Notificación',
  CREATE_PURCHASE_ORDER: 'Crear Pedido',
  UPDATE_PRIORITY: 'Actualizar Prioridad',
  SEND_EMAIL: 'Enviar Email',
  LOG_EVENT: 'Registrar Evento',
};

export default function BusinessRules() {
  const { api } = useApi();
  const { showSuccess, showError } = useToast();
  const { canEdit } = usePermissions();
  const [rules, setRules] = useState<BusinessRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<BusinessRule | null>(null);

  const [formData, setFormData] = useState<BusinessRule>({
    name: '',
    description: '',
    trigger_type: 'STOCK_BELOW_MIN',
    conditions: [],
    actions: [{ type: 'CREATE_NOTIFICATION', params: { title: '', message: '' } }],
    is_active: true,
    priority: 0,
  });

  useEffect(() => {
    loadRules();
  }, []);

  const loadRules = async () => {
    try {
      setLoading(true);
      const response = await api.get('/rules');
      setRules(response.data);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al cargar reglas');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    setEditingRule(null);
    setFormData({
      name: '',
      description: '',
      trigger_type: 'STOCK_BELOW_MIN',
      conditions: [],
      actions: [{ type: 'CREATE_NOTIFICATION', params: { title: '', message: '' } }],
      is_active: true,
      priority: 0,
    });
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (rule: BusinessRule) => {
    setEditingRule(rule);
    setFormData(rule);
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingRule?.id) {
        await api.put(`/rules/${editingRule.id}`, formData);
        showSuccess('Regla actualizada correctamente');
      } else {
        await api.post('/rules', formData);
        showSuccess('Regla creada correctamente');
      }
      setIsModalOpen(false);
      loadRules();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al guardar regla');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Estás seguro de que quieres eliminar esta regla?')) {
      return;
    }

    try {
      await api.delete(`/rules/${id}`);
      showSuccess('Regla eliminada correctamente');
      loadRules();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al eliminar regla');
    }
  };

  const handleAddAction = () => {
    setFormData({
      ...formData,
      actions: [
        ...formData.actions,
        { type: 'CREATE_NOTIFICATION', params: { title: '', message: '' } },
      ],
    });
  };

  const handleRemoveAction = (index: number) => {
    setFormData({
      ...formData,
      actions: formData.actions.filter((_, i) => i !== index),
    });
  };

  const handleUpdateAction = (index: number, action: RuleAction) => {
    const newActions = [...formData.actions];
    newActions[index] = action;
    setFormData({ ...formData, actions: newActions });
  };

  const handleTestRule = async () => {
    try {
      await api.post('/rules/check/stock-below-min');
      showSuccess('Verificación de stock bajo ejecutada. Revisa las notificaciones.');
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al ejecutar verificación');
    }
  };

  return (
    <div className="tab-content">
      <div className="flex-between mb-2">
        <h2>Reglas de Negocio</h2>
        <div className="flex-gap">
          <button
            className="btn btn-secondary"
            onClick={handleTestRule}
            title="Ejecutar verificación de stock bajo"
          >
            🔍 Verificar Stock Bajo
          </button>
          {canEdit('settings') && (
            <button className="btn btn-primary" onClick={handleOpenCreateModal}>
              + Nueva Regla
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <SkeletonTable rows={5} columns={5} />
      ) : (
        <>
          {rules.length === 0 ? (
            <EmptyStateNoData />
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Trigger</th>
                    <th>Acciones</th>
                    <th>Prioridad</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((rule) => (
                    <tr key={rule.id}>
                      <td>
                        <div>
                          <strong>{rule.name}</strong>
                          {rule.description && (
                            <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                              {rule.description}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="badge badge-info">
                          {TRIGGER_LABELS[rule.trigger_type]}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                          {rule.actions.map((action, idx) => (
                            <span key={idx} className="badge badge-secondary" style={{ fontSize: '0.75rem' }}>
                              {ACTION_LABELS[action.type]}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>{rule.priority}</td>
                      <td>
                        <span
                          className={`badge ${rule.is_active ? 'badge-success' : 'badge-secondary'}`}
                        >
                          {rule.is_active ? 'Activa' : 'Inactiva'}
                        </span>
                      </td>
                      <td>
                        <div className="flex gap-1">
                          {canEdit('settings') && (
                            <>
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => handleOpenEditModal(rule)}
                                title="Editar"
                              >
                                ✏️
                              </button>
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => handleDelete(rule.id!)}
                                title="Eliminar"
                              >
                                🗑️
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Modal Crear/Editar Regla */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingRule ? 'Editar Regla' : 'Nueva Regla'}
        size="large"
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => setIsModalOpen(false)}
            >
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              data-modal-default="true"
            >
              {editingRule ? 'Actualizar' : 'Crear'}
            </button>
          </>
        }
      >
        <div className="form-grid">
          <div className="input-group">
            <label className="input-label">Nombre de la Regla</label>
            <input
              type="text"
              className="input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Ej: Alerta de Stock Bajo"
            />
          </div>

          <div className="input-group">
            <label className="input-label">Descripción</label>
            <textarea
              className="input"
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Descripción opcional de la regla"
              rows={2}
            />
          </div>

          <div className="input-group">
            <label className="input-label">Trigger (Evento)</label>
            <select
              className="input"
              value={formData.trigger_type}
              onChange={(e) => setFormData({ ...formData, trigger_type: e.target.value as RuleTriggerType })}
            >
              {Object.entries(TRIGGER_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div className="input-group">
            <label className="input-label">Prioridad</label>
            <input
              type="number"
              className="input"
              min="0"
              max="100"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
            />
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Mayor prioridad = se ejecuta primero
            </small>
          </div>

          <div className="input-group">
            <label className="input-label">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
              {' '}Activa
            </label>
          </div>
        </div>

        <div style={{ marginTop: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h4>Acciones</h4>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={handleAddAction}
            >
              + Agregar Acción
            </button>
          </div>

          {formData.actions.map((action, index) => (
            <div key={index} className="rule-action-item">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <select
                  className="input"
                  value={action.type}
                  onChange={(e) =>
                    handleUpdateAction(index, {
                      ...action,
                      type: e.target.value as RuleActionType,
                      params: {},
                    })
                  }
                  style={{ width: 'auto', minWidth: '200px' }}
                >
                  {Object.entries(ACTION_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={() => handleRemoveAction(index)}
                >
                  Eliminar
                </button>
              </div>

              {action.type === 'CREATE_NOTIFICATION' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <input
                    type="text"
                    className="input"
                    placeholder="Título de la notificación"
                    value={action.params.title || ''}
                    onChange={(e) =>
                      handleUpdateAction(index, {
                        ...action,
                        params: { ...action.params, title: e.target.value },
                      })
                    }
                  />
                  <textarea
                    className="input"
                    placeholder="Mensaje (puedes usar {nombre}, {cod_item}, {current_stock}, etc.)"
                    value={action.params.message || ''}
                    onChange={(e) =>
                      handleUpdateAction(index, {
                        ...action,
                        params: { ...action.params, message: e.target.value },
                      })
                    }
                    rows={3}
                  />
                  <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                    Variables disponibles: {'{nombre}'}, {'{cod_item}'}, {'{current_stock}'}, {'{min_stock}'}, {'{location_name}'}
                  </small>
                </div>
              )}
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}

